import { RefreshCw01 } from "@untitledui/icons";
import type { useAdminAds } from "@/api/admin/ads";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { Table, TableCard } from "@/components/application/table/table";
import type { BadgeColor } from "@/components/base/badges/badges";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import type { useSort } from "@/hooks/use-sort";
import type { CheckedStatus } from "@/pages/admin/ads-page";
import { formatDate } from "@/utils/date-conversion";

type AdsData = NonNullable<ReturnType<typeof useAdminAds>["data"]>;
type AdsBrand = AdsData["brands"][number];

type Descriptor = { label: string; color: BadgeColor<"pill-color">; title?: string };

const NOT_CHECKED: Descriptor = { label: "Not checked", color: "gray" };
const CHECKING: Descriptor = { label: "Checking…", color: "blue" };

// Common state for the three live signals: while a check is in flight show "Checking…",
// a check that threw shows "Failed" (with the error), and one that never ran stays
// "Not checked". Returns null when there's a real status to describe.
const pendingDescriptor = (checked: CheckedStatus | undefined, isRefreshing: boolean): Descriptor | null => {
  if (isRefreshing) return CHECKING;
  if (!checked) return NOT_CHECKED;
  if (!checked.ok) return { label: "Failed", color: "error", title: checked.error };
  return null;
};

// Signal 1: is the brand actually authed (TikTok accepts the token, not just that one exists).
const authedDescriptor = (checked: CheckedStatus | undefined, isRefreshing: boolean): Descriptor => {
  const pending = pendingDescriptor(checked, isRefreshing);
  if (pending || !checked?.ok) return pending ?? NOT_CHECKED;
  return checked.status.authed
    ? { label: "Authed", color: "success" }
    : { label: "Not authed", color: "gray", title: checked.status.error ?? undefined };
};

// Signal 2: do we hold all the scopes we need? Named and numeric-format tokens are both
// asserted; a token reporting no scopes at all cannot be judged either way.
const permissionsDescriptor = (checked: CheckedStatus | undefined, isRefreshing: boolean): Descriptor => {
  const pending = pendingDescriptor(checked, isRefreshing);
  if (pending || !checked?.ok) return pending ?? NOT_CHECKED;
  const status = checked.status;
  if (!status.authed) return { label: "—", color: "gray" };
  if (status.scopeFormat === "unknown") return { label: "Unknown", color: "gray" };
  return status.missingScopes.length === 0
    ? { label: "All granted", color: "success" }
    : { label: `Missing ${status.missingScopes.length}`, color: "error", title: status.missingScopes.join(", ") };
};

const advertiserPinDescriptor = (brand: AdsBrand): Descriptor =>
  brand.advertiserId
    ? { label: "Pinned", color: "success", title: brand.advertiserId }
    : { label: "Not confirmed", color: "warning", title: "No advertiser confirmed by the merchant" };

// Signal 3: does the live /advertiser/info/ call currently succeed?
const accessDescriptor = (checked: CheckedStatus | undefined, isRefreshing: boolean): Descriptor => {
  const pending = pendingDescriptor(checked, isRefreshing);
  if (pending || !checked?.ok) return pending ?? NOT_CHECKED;
  const status = checked.status;
  if (!status.authed) return { label: "—", color: "gray" };
  if (status.connected) return { label: "Yes", color: "success" };
  if (status.needsReauth) return { label: "Needs reauth", color: "warning", title: status.error ?? undefined };
  return { label: "No", color: "error", title: status.error ?? undefined };
};

// Signal 4: has the nightly pipeline actually landed GMV Max rows for this brand?
const dataDescriptor = (brand: AdsBrand): Descriptor => {
  if (brand.gmvMax.gmvMaxActiveRows > 0) {
    return { label: `Yes (${brand.gmvMax.gmvMaxActiveRows})`, color: "success" };
  }
  return { label: "No", color: "gray" };
};

const StatusBadge = ({ descriptor }: { descriptor: Descriptor }) =>
  descriptor.label === "—" ? (
    <span className="text-sm text-tertiary" title={descriptor.title}>
      —
    </span>
  ) : (
    <span title={descriptor.title}>
      <Badge color={descriptor.color}>{descriptor.label}</Badge>
    </span>
  );

type AdminAdsTableProps = {
  brands: AdsBrand[];
  statuses: Map<string, CheckedStatus>;
  sort: ReturnType<typeof useSort<"id" | "name" | "createdAt">>;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRefresh: (merchantId: string) => void;
  refreshingIds: Set<string>;
};

export const AdminAdsTable = ({
  brands,
  statuses,
  sort,
  page,
  totalPages,
  onPageChange,
  onRefresh,
  refreshingIds,
}: AdminAdsTableProps) => {
  return (
    <TableCard.Root className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-auto">
        <Table aria-label="Ads" sortDescriptor={sort.descriptor} onSortChange={sort.setFromDescriptor}>
          <Table.Header>
            <Table.Head id="id" label="Brand" isRowHeader allowsSorting className="w-[20%]" />
            <Table.Head
              id="authed"
              label="Authed"
              className="w-[11%]"
              tooltip="Has this brand connected TikTok Ads? 'Authed' means we hold a working token (stored in S3, decrypts, and grants at least one ad account). 'Not authed' means no token yet, or it's missing/broken — hover the badge for the exact reason."
            />
            <Table.Head
              id="permissions"
              label="Permissions"
              className="w-[13%]"
              tooltip="Does the brand's token grant all the ad permissions we need? 'All granted' is good; 'Missing N' means they must re-authorise (hover to see which); 'Legacy — verify' is an old token whose permissions we can't read automatically, so check by hand."
            />
            <Table.Head
              id="access"
              label="GMV Max access"
              className="w-[13%]"
              tooltip="Result of a live call to TikTok right now using this token. 'Yes' = we can reach their ad account; 'Needs reauth' = the token expired or was revoked; '—' = shown when the brand isn't authed."
            />
            <Table.Head
              id="advertiser"
              label="Advertiser"
              className="w-[11%]"
              tooltip="Whether the brand confirmed which advertiser account Unbox may act on. 'Not confirmed' means the Integrations page shows them as incomplete — either they never finished the new authorization flow, or they authorised through the legacy link."
            />
            <Table.Head
              id="data"
              label="GMV Max data"
              className="w-[13%]"
              tooltip="Whether our nightly pipeline has loaded active GMV Max campaign rows for this brand. This is separate from the auth checks — it reflects data already in our warehouse, not a live TikTok call. The count is how many active rows."
            />
            <Table.Head
              id="checked"
              label="Last checked"
              className="w-[12%]"
              tooltip="When the auth columns (Authed, Permissions, GMV Max access) were last refreshed. These run on demand — click Refresh to check. 'GMV Max data' is always current."
            />
            <Table.Head id="refresh" label="" className="w-[10%]" />
          </Table.Header>

          {/* `dependencies` is required: rows read `statuses`/`refreshingIds` (external to
              `items`), and without it react-aria caches rows by brand identity and never
              re-renders when a refresh result lands - the row stays on "Not checked". */}
          <Table.Body items={brands} dependencies={[statuses, refreshingIds]}>
            {(brand) => {
              const checked = statuses.get(brand.merchantId);
              const isRefreshing = refreshingIds.has(brand.merchantId);
              return (
                <Table.Row id={brand.merchantId}>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-primary">{brand.name}</p>
                      <p className="text-sm text-tertiary">ID: {brand.merchantId}</p>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <StatusBadge descriptor={authedDescriptor(checked, isRefreshing)} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <StatusBadge descriptor={permissionsDescriptor(checked, isRefreshing)} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <StatusBadge descriptor={accessDescriptor(checked, isRefreshing)} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <StatusBadge descriptor={advertiserPinDescriptor(brand)} />
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="flex flex-col gap-0.5">
                      <StatusBadge descriptor={dataDescriptor(brand)} />
                      {brand.gmvMax.lastSyncedAt && (
                        <span className="text-xs text-tertiary">synced {formatDate(brand.gmvMax.lastSyncedAt)}</span>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap text-sm text-tertiary">
                    {isRefreshing ? "Checking…" : checked ? formatDate(checked.checkedAt.toISOString()) : "Never"}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <Button
                      size="sm"
                      color="secondary"
                      iconLeading={RefreshCw01}
                      isLoading={refreshingIds.has(brand.merchantId)}
                      isDisabled={refreshingIds.has(brand.merchantId)}
                      onClick={() => onRefresh(brand.merchantId)}
                    >
                      Refresh
                    </Button>
                  </Table.Cell>
                </Table.Row>
              );
            }}
          </Table.Body>
        </Table>
      </div>

      <PaginationPageMinimalCenter
        page={page}
        total={totalPages}
        onPageChange={onPageChange}
        className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
      />
    </TableCard.Root>
  );
};
